import { useAuth } from './auth'

/**
 * Cliente HTTP mínimo (sin dependencias): añade el Bearer, reintenta una vez
 * tras refrescar la sesión si el token venció, y traduce errores del backend
 * a un tipo uniforme. Todas las páginas pasan por aquí — nunca `fetch`
 * directo — para que la renovación de sesión sea un único punto.
 */
export class ErrorApi extends Error {
  constructor(
    public estado: number,
    public codigo: string,
    mensaje: string,
    public detalle?: unknown,
  ) {
    super(mensaje)
  }
}

let refrescando: Promise<boolean> | null = null

async function refrescar(): Promise<boolean> {
  refrescando ??= (async () => {
    try {
      const r = await fetch('/api/v1/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-Rumbo': '1' },
      })
      if (!r.ok) return false
      const cuerpo = await r.json()
      useAuth.getState().establecerSesion(cuerpo)
      return true
    } catch {
      return false
    } finally {
      refrescando = null
    }
  })()
  return refrescando
}

interface Opciones {
  metodo?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  cuerpo?: unknown
  consulta?: Record<string, string | number | boolean | undefined | null>
  binario?: boolean
  reintentar?: boolean
}

function armarUrl(ruta: string, consulta?: Opciones['consulta']): string {
  const url = new URL(ruta, window.location.origin)
  if (consulta) {
    for (const [k, v] of Object.entries(consulta)) {
      if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v))
    }
  }
  return url.pathname + url.search
}

export async function api<T = unknown>(ruta: string, opciones: Opciones = {}): Promise<T> {
  const token = useAuth.getState().accessToken
  const cabeceras: Record<string, string> = {}
  if (token) cabeceras.Authorization = `Bearer ${token}`
  if (opciones.cuerpo !== undefined) cabeceras['Content-Type'] = 'application/json'

  const r = await fetch(armarUrl(`/api/v1${ruta}`, opciones.consulta), {
    method: opciones.metodo ?? 'GET',
    credentials: 'include',
    headers: cabeceras,
    body: opciones.cuerpo !== undefined ? JSON.stringify(opciones.cuerpo) : undefined,
  })

  if (r.status === 401 && token && opciones.reintentar !== false) {
    const dat = await r.clone().json().catch(() => null)
    if (dat?.error === 'TOKEN_EXPIRADO' && (await refrescar())) {
      return api<T>(ruta, { ...opciones, reintentar: false })
    }
    useAuth.getState().cerrarSesion()
  }

  if (!r.ok) {
    const cuerpo = await r.json().catch(() => ({ error: 'ERROR', mensaje: r.statusText }))
    throw new ErrorApi(r.status, cuerpo.error ?? 'ERROR', cuerpo.mensaje ?? 'Ocurrió un error', cuerpo.detalle)
  }
  if (opciones.binario) return (await r.blob()) as T
  if (r.status === 204) return undefined as T
  return r.json() as Promise<T>
}

export const obtener = <T>(ruta: string, consulta?: Opciones['consulta']) => api<T>(ruta, { consulta })
export const enviar = <T>(ruta: string, cuerpo?: unknown, metodo: Opciones['metodo'] = 'POST') => api<T>(ruta, { metodo, cuerpo })
export const eliminar = <T>(ruta: string) => api<T>(ruta, { metodo: 'DELETE' })
